import { FileText, Loader2, Table2, Search, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { toast, Zoom } from "react-toastify";
import createSocketToToast from "~/components/SocketToToast";
import api, { baseURL } from "~/services/api";
import useSocketStore from "~/store/socketStore";

function EvaluationChart() {
  const [loading, setLoading] = useState(false);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [error, setError] = useState(false);
  const [groups, setGroups] = useState([]);
  const { socket } = useSocketStore();
  const [progresso, setProgresso] = useState(0);
  const [progresso2, setProgresso2] = useState(0);

  const [searchText, setSearchText] = useState("");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const dropdownRef = useRef();

  function handleGenerateReport() {
    setError(false);
    setLoading(true);
    const group_id = selectedGroup?.id;

    if (!group_id) {
      setLoading(false);
      setTimeout(() => {
        toast("Group is required!", {
          autoClose: 1500,
          type: "error",
          transition: Zoom,
        });
        setError("group_id");
      }, 300);
      return;
    }
    createSocketToToast(
      socket,
      "progresso_arquivo_evaluation_chart_excel",
      "Evaluation Chart - Excel",
      setProgresso
    );

    api
      .post(`/reports/evaluationChart`, {
        group_id,
        socketId: socket.id,
      })
      .then(({ data }) => {
        saveAs(`${baseURL}/get-file/${data.name}`, `${data.name}.xlsx`);
        setLoading(false);
        setProgresso(0);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        setProgresso(0);
      });
  }

  function handleGeneratePDF() {
    setError(false);
    setLoadingPDF(true);
    const group_id = selectedGroup?.id;

    if (!group_id) {
      setLoadingPDF(false);
      setTimeout(() => {
        toast("Group is required!", {
          autoClose: 1500,
          type: "error",
          transition: Zoom,
        });
        setError("group_id");
      }, 300);
      return;
    }
    createSocketToToast(
      socket,
      "progresso_arquivo_evaluation_chart_pdf",
      "Evaluation Chart - PDF",
      setProgresso2
    );

    api
      .post(
        `/reports/evaluationChartPDF`,
        {
          group_id,
          socketId: socket.id,
        },
        {
          responseType: "blob",
        }
      )
      .then(({ data }) => {
        const pdfBlob = new Blob([data], { type: "application/pdf" });
        saveAs(pdfBlob, `evaluation_chart_${group_id}.pdf`);
        setLoadingPDF(false);
      })
      .catch((err) => {
        console.log(err);
        setLoadingPDF(false);
      });
  }

  async function getGroups() {
    try {
      const { data } = await api.get("/studentgroups?limit=1000");
      const mappedGroups = data.rows.map((group) => ({ id: group.id, name: group.name }));
      setGroups(mappedGroups);
      setFilteredGroups(mappedGroups);
    } catch (err) {
      console.log(err);
    }
  }

  
  useEffect(() => {
    if (searchText.trim() === "") {
      setFilteredGroups(groups);
    } else {
      const filtered = groups.filter((group) =>
        group.name.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredGroups(filtered);
    }
  }, [searchText, groups]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setError(null);
    }, 3000);
    getGroups();
  }, [error]);

  function handleSelectGroup(group) {
    setSelectedGroup(group);
    setSearchText(group.name);
    setIsDropdownOpen(false);
    setError(false);
  }

  function handleClearSelection() {
    setSelectedGroup(null);
    setSearchText("");
    setFilteredGroups(groups);
    setIsDropdownOpen(false);
  }

  function handleInputFocus() {
    setIsDropdownOpen(true);
  }

  function handleInputChange(e) {
    setSearchText(e.target.value);
    setSelectedGroup(null);
    setIsDropdownOpen(true);
  }

  return (
    <div className="flex flex-col items-center gap-2 border rounded-lg hover:scale-105 duration-300 ease-in-out min-w-[500px]">
      <h2 className="text-md font-bold border-b w-full text-center bg-zinc-100 px-2 py-1 text-primary">
        Evaluation Chart
      </h2>
      <div className="w-full flex flex-row items-end gap-2 p-2">
        <div className="flex flex-1 flex-col gap-2">
          <label className="text-xs font-bold flex-1 text-center">Group</label>
          <div className="relative w-full" ref={dropdownRef}>
            <div className="relative">
              <input
                type="text"
                className={`w-full transition ease-in-out duration-300 text-xs bg-white text-gray-500 p-2 pl-8 pr-8 border rounded ${
                  error === "group_id" ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Search for a group..."
                value={searchText}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
              />
              <Search size={14} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
              {selectedGroup && (
                <button
                  type="button"
                  onClick={handleClearSelection}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {isDropdownOpen && filteredGroups.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
                {filteredGroups.map((group) => (
                  <div
                    key={group.id}
                    className={`px-3 py-2 text-xs cursor-pointer hover:bg-blue-50 ${
                      selectedGroup?.id === group.id ? "bg-blue-100 font-semibold" : ""
                    }`}
                    onClick={() => handleSelectGroup(group)}
                  >
                    {group.name}
                  </div>
                ))}
              </div>
            )}

            {isDropdownOpen && filteredGroups.length === 0 && searchText && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg">
                <div className="px-3 py-2 text-xs text-gray-500 text-center">
                  No groups found
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            disabled={loading}
            onClick={() => handleGenerateReport()}
            className="text-md font-bold bg-secondary border text-zinc-500 hover:border-primary hover:text-primary rounded-md p-4 h-6 flex flex-row items-center justify-center text-xs gap-1"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span className="text-xs">{progresso}%</span>
              </>
            ) : (
              <>
                <Table2 size={16} />
                Generate Worksheet
              </>
            )}
          </button>
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            disabled={loadingPDF}
            onClick={() => handleGeneratePDF()}
            className="text-md font-bold bg-secondary border text-zinc-500 hover:border-primary hover:text-primary rounded-md p-4 h-6 flex flex-row items-center justify-center text-xs gap-1"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span className="text-xs">{progresso2}%</span>
              </>
            ) : (
              <>
                <FileText size={16} />
                PDF
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EvaluationChart;
